// Israeli IBAN support, built on top of the shared BBAN layout (lib/bban).
// An Israeli IBAN is: 'IL' + 2 check digits + the 19-digit BBAN
// (bank code 3 + branch code 3 + account number 13). The 2 check digits are the
// ISO 13616 (mod-97-10) IBAN check digits, computed here; the BBAN itself is
// assembled/split by lib/bban. Only Israel is supported for now — foreign IBANs
// have country-specific BBAN layouts and are out of scope.
import { assembleBban, splitBban, type BbanParts } from 'lib/bban';

// Country code (2) + check digits (2) precede the BBAN.
export const IBAN_PREFIX_LENGTH = 4;
export const IBAN_COUNTRY = 'IL';

// Convert letters to their IBAN numeric value (A=10 .. Z=35); digits pass through.
const toNumericString = (value: string): string =>
  [...value.toUpperCase()]
    .map((ch) => {
      const code = ch.charCodeAt(0);
      return code >= 65 && code <= 90 ? String(code - 55) : ch;
    })
    .join('');

// mod-97 over an arbitrarily long numeric string, computed piecewise so we never
// overflow Number.
const mod97 = (numeric: string): number =>
  [...numeric].reduce((acc, digit) => (acc * 10 + Number(digit)) % 97, 0);

// The 2 IBAN check digits (positions 3–4): move country code + '00' to the end,
// convert to digits, then 98 - (mod 97).
export const computeIbanCheckDigits = (countryCode: string, bban: string): string =>
  String(98 - mod97(toNumericString(`${bban}${countryCode}00`))).padStart(2, '0');

const normalizeIban = (iban: string): string => (iban ?? '').replace(/\s+/g, '').toUpperCase();

// Structural + checksum validity (ISO 7064 mod-97-10): move the first 4 chars to
// the end, convert to digits, the whole thing mod 97 must equal 1.
export const isValidIban = (iban: string): boolean => {
  const value = normalizeIban(iban);
  if (value.length < IBAN_PREFIX_LENGTH) return false;
  return mod97(toNumericString(value.slice(IBAN_PREFIX_LENGTH) + value.slice(0, IBAN_PREFIX_LENGTH))) === 1;
};

// Assemble an Israeli IBAN from BBAN parts: zero-pad + concatenate into the
// 19-digit BBAN (via lib/bban), then prepend 'IL' + the computed check digits.
export const assembleIsraeliIban = (parts: BbanParts): string => {
  const bban = assembleBban(parts);
  return `${IBAN_COUNTRY}${computeIbanCheckDigits(IBAN_COUNTRY, bban)}${bban}`;
};

// A well-formed Israeli IBAN: 'IL' + 21 digits (2 check + 19 BBAN).
const ISRAELI_IBAN_REGEX = /^IL\d{21}$/;

// Split a whole Israeli IBAN back into its BBAN parts: drop the 4-char prefix
// ('IL' + check digits), then split the remaining 19-digit BBAN via lib/bban.
// Guards on the IL structure first — otherwise any string would be sliced into
// garbage parts (e.g. prefilling bank/branch/account from a non-IL value in edit
// mode); returns empty parts when the input is not a well-formed Israeli IBAN.
export const splitIsraeliIban = (iban: string): BbanParts => {
  const value = normalizeIban(iban);
  if (!ISRAELI_IBAN_REGEX.test(value)) return { bankCode: '', branchCode: '', accountNumber: '' };
  return splitBban(value.slice(IBAN_PREFIX_LENGTH));
};
